import { cn } from '@forest/shared'
import {
  createMemo,
  createSignal,
  For,
  onMount,
  Show,
  useContext,
  type Accessor,
  type JSX,
} from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import { resolveCountdownTier } from '~/lib/countdown'
import { logNonFatal } from '~/lib/diagnostics'
import { intlLocale } from '~/lib/params'
import type { RemoteArtworkStatus } from '~/lib/remote-image'
import { m } from '~/paraglide/messages'
import type { CatalogueContent, CatalogueProduct } from '~/services/catalogue'
import type { HeroDisplay } from '~/services/main-products'
import type { HeroSlot } from '~/store/hero-row'
import type { StarterPack } from '~/services/starter-pack'
import type { StarterPackSlot } from '~/store/starter-pack'
import {
  createCataloguePriceParts,
  freePriceLabel,
  StruckOriginalPrice,
} from './effective-price-parts'
import { HeroBannersContext } from './hero-banners'
import { CountdownIcon } from './icons/countdown'
import { PLACEHOLDER_BAR_CLASS } from './placeholder-bar'
import { StarterPackShimmer } from './starter-pack-shimmer'

const numberFormatter = new Intl.NumberFormat(intlLocale)

// The row's layout and each card's footprint — a 364px card, 296px wide on
// the scroll or the row's width alone — shared by the real row and the boot
// skeleton's (GLOSSARY.md "Boot skeleton").
const HERO_ROW_SECTION_CLASS = 'pb-6'
const HERO_ROW_LIST_CLASS =
  'flex gap-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
const HERO_SLOT_CLASS = {
  fill: 'w-full',
  scroll: 'w-[296px] shrink-0',
} as const
const HERO_CARD_FRAME_CLASS =
  'gradient-outline relative h-[364px] w-full overflow-hidden rounded-[38px] bg-white/35 [--ow-from:rgba(255,255,255,0.5)] [--ow-to:rgba(255,255,255,0.3)] [--ow:2px]'

// The pending slot's bar where the price pill will land, over the card's
// banner layers: the design's hero skeleton, which draws no title bar.
function HeroPendingBars(props: { testId?: string }) {
  return (
    <div
      data-testid={props.testId}
      aria-hidden="true"
      class="absolute inset-0 flex animate-pulse flex-col justify-end p-6"
    >
      <div class={cn(PLACEHOLDER_BAR_CLASS, 'h-10 w-full rounded-3xl')} />
    </div>
  )
}

function HeroOwnedPill(props: { testId?: string }) {
  return (
    <span
      data-testid={props.testId}
      class="relative flex h-10 w-full items-center justify-center gap-0.5 rounded-3xl bg-white/40 text-base font-bold text-white"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.49341 11L9.16012 14.6667L16.5065 7.33325"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      {m.owned_tag()}
    </span>
  )
}

// A free hero shows "Free" alone (GLOSSARY.md "Free product").
function HeroPricePill(props: {
  product: CatalogueProduct
  serverNow: Accessor<number>
}) {
  const paid = createCataloguePriceParts(
    () => props.product.price,
    () => props.serverNow(),
  )
  return (
    <span
      data-testid="hero-price"
      data-amount={paid()?.amount}
      data-original={paid()?.original ?? undefined}
      class="relative flex h-10 w-full items-center justify-center gap-1 rounded-3xl bg-white/90 px-2 text-base font-bold text-[#00524F]"
    >
      <Show when={paid()} fallback={freePriceLabel()}>
        {(parts) => (
          <>
            <img
              alt=""
              class="size-4"
              src={parts().currency === 'coin' ? coinIcon : gemIcon}
            />
            <StruckOriginalPrice
              original={parts().original}
              class="text-[#00524F4D] line-through"
            />
            {numberFormatter.format(parts().amount)}
          </>
        )}
      </Show>
    </span>
  )
}

// The hero countdown (GLOSSARY.md): the card's badge counting down to its
// end on the Shop server clock, in the rounded-up day, hour, or minute tier
// and the metadata's tag colour. No end, no badge.
function HeroCountdown(props: {
  color: string
  endMs: number | undefined
  serverNow: Accessor<number>
}) {
  const copy = createMemo(() => {
    const endMs = props.endMs
    if (endMs === undefined) return null
    const tier = resolveCountdownTier(endMs - props.serverNow())
    if (tier.unit === 'day') return m.featured_day({ number: tier.count })
    if (tier.unit === 'hour') return m.featured_hour({ number: tier.count })
    return m.featured_min({ number: tier.count })
  })
  return (
    <Show when={copy()}>
      {(text) => (
        <span
          data-testid="hero-countdown"
          style={{ 'background-color': props.color }}
          class="absolute left-5 top-5 flex items-center rounded-[30px] border border-[rgba(255,255,255,0.49)] px-2.5 py-1.5 text-sm font-medium leading-4 text-white"
        >
          <CountdownIcon />
          {text()}
        </span>
      )}
    </Show>
  )
}

// The settled card's tap target over the banner layers: title, price pill or
// owned tag, and the hero countdown. Card body and price pill are one target
// and both open the product detail.
function HeroCardBody(props: {
  hero: HeroSlot
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  onSelect: (product: CatalogueProduct) => void
  product: CatalogueProduct
  serverNow: Accessor<number>
}) {
  const isOwned = () =>
    props.isOwned?.(props.hero.slot.content, props.hero.slot.gid) ??
    props.product.owned

  return (
    <button
      type="button"
      data-testid="hero-product"
      data-content={props.hero.slot.content}
      data-gid={props.hero.slot.gid}
      onClick={() => props.onSelect({ ...props.product, owned: isOwned() })}
      class="absolute inset-0 flex flex-col justify-end gap-4 p-6 text-left"
    >
      <span
        data-testid="hero-title"
        class="relative line-clamp-2 text-xl font-semibold leading-7 text-white"
      >
        {props.product.title}
      </span>
      {isOwned() ? (
        <HeroOwnedPill />
      ) : (
        <HeroPricePill product={props.product} serverNow={props.serverNow} />
      )}
      <HeroCountdown
        color={props.hero.display.countdownColor}
        endMs={props.hero.purchaseEndAtMs}
        serverNow={props.serverNow}
      />
    </button>
  )
}

// One hero card's frame for the slot's whole life — pending and settled are
// the same element. The banner starts downloading the moment the card
// exists, beneath the blurred banner placeholder (GLOSSARY.md) or the plain
// card fill, and fades in once paintable — whether or not the slot has
// settled. Over the banner layers sit the children: the pending bars, then
// the settled card body. A banner that fails to load leaves its placeholder
// (or the fill) in place and keeps the card (GLOSSARY.md "Display metadata").
function HeroCardFrame(props: {
  children: JSX.Element
  display: HeroDisplay
  // Banner-failure diagnostics: the Store product, and the content gid for
  // a main product.
  gid?: number
  productId: number
  // Layers drawn over the children, given the banner's load status: the
  // starter pack shimmer waits for a loaded banner.
  overlay?: (bannerStatus: Accessor<RemoteArtworkStatus>) => JSX.Element
  testId: string
}) {
  const [bannerStatus, setBannerStatus] =
    createSignal<RemoteArtworkStatus>('loading')
  const banners = useContext(HeroBannersContext)
  let banner: HTMLImageElement | undefined
  // A banner in the browser's memory cache (a return to the Featured tab
  // remounts the row) is complete before the first paint: shown at once,
  // with no fade to sit through. A disk-cache hit still loads, and fades.
  onMount(() => {
    if (banner?.complete && banner.naturalWidth > 0) setBannerStatus('loaded')
    // The reveal waits on this card's banner while it is mounted
    // (GLOSSARY.md "Reveal surface").
    banners?.track(bannerStatus)
  })
  const reportBannerFailure = () => {
    setBannerStatus('failed')
    logNonFatal('shop_hero_banner_failed', {
      ...(props.gid === undefined ? {} : { gid: String(props.gid) }),
      product_id: String(props.productId),
      url: props.display.bannerUrl,
    })
  }

  return (
    <div
      data-testid={props.testId}
      data-gid={props.gid}
      data-banner-status={bannerStatus()}
      class={HERO_CARD_FRAME_CLASS}
    >
      <Show when={props.display.placeholderSrc}>
        {(src) => (
          <img
            data-testid="hero-banner-placeholder"
            alt=""
            aria-hidden="true"
            src={src()}
            // Scaled up so the blur's transparent fringe falls outside the
            // frame's clip.
            class="absolute inset-0 size-full scale-110 object-cover blur-[12px]"
          />
        )}
      </Show>
      <Show when={bannerStatus() !== 'failed'}>
        <img
          ref={(element) => {
            banner = element
          }}
          data-testid="hero-banner"
          alt=""
          src={props.display.bannerUrl}
          onLoad={() => setBannerStatus('loaded')}
          onError={reportBannerFailure}
          class="absolute inset-0 size-full object-cover transition-opacity duration-300 ease-out"
          classList={{ 'opacity-0': bannerStatus() !== 'loaded' }}
        />
      </Show>
      {props.children}
      {props.overlay?.(bannerStatus)}
    </div>
  )
}

// One main-product hero slot's card.
function HeroCard(props: {
  hero: HeroSlot
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  onSelect: (product: CatalogueProduct) => void
  serverNow: Accessor<number>
}) {
  return (
    <HeroCardFrame
      display={props.hero.display}
      gid={props.hero.slot.gid}
      productId={props.hero.productId}
      testId="hero-card"
    >
      <Show
        when={props.hero.slot.product}
        fallback={<HeroPendingBars testId="hero-slot-skeleton" />}
      >
        {(product) => (
          <HeroCardBody
            hero={props.hero}
            isOwned={props.isOwned}
            onSelect={props.onSelect}
            product={product()}
            serverNow={props.serverNow}
          />
        )}
      </Show>
    </HeroCardFrame>
  )
}

// The starter pack's card (GLOSSARY.md "Starter pack (新手禮包)"), leading the
// row until owned and trailing it after: the hero frame and countdown rules,
// with fixed web copy for its title and description and the Shop IAP price
// in its pill. Unowned, it counts down to the entry's per-user
// eligible_until; owned, it shows the owned tag and no countdown. The card
// body opens the starter-pack detail; the copy is display over it, so a tap
// there lands on the body. The price pill is its own button and buys the
// pack straight away, with no web confirmation.
function StarterPackCard(props: {
  onPurchase: (pack: StarterPack) => void
  onSelect: (pack: StarterPack) => void
  serverNow: Accessor<number>
  shimmerHeld: () => boolean
  slot: StarterPackSlot
}) {
  return (
    <HeroCardFrame
      display={props.slot.candidate.display}
      productId={props.slot.candidate.product.id}
      testId="starter-pack-card"
      overlay={(bannerStatus) => (
        <Show
          when={
            bannerStatus() === 'loaded' &&
            props.slot.pack &&
            !props.slot.pack.owned &&
            !props.shimmerHeld()
          }
        >
          <StarterPackShimmer />
        </Show>
      )}
    >
      <Show
        when={props.slot.pack}
        fallback={<HeroPendingBars testId="hero-slot-skeleton" />}
      >
        {(pack) => (
          <>
            <button
              type="button"
              data-testid="starter-pack-body"
              aria-label={m.starter_pack_title()}
              onClick={() => props.onSelect(pack())}
              class="absolute inset-0"
            />
            <div class="pointer-events-none absolute inset-0 flex flex-col justify-end gap-4 p-6 text-left">
              <div class="relative flex flex-col gap-1 text-white">
                <span
                  data-testid="starter-pack-title"
                  class="line-clamp-2 text-xl font-semibold leading-7"
                >
                  {m.starter_pack_title()}
                </span>
                <span
                  data-testid="starter-pack-description"
                  class="line-clamp-2 text-sm font-medium leading-5"
                >
                  {m.starter_pack_featured_card_content()}
                </span>
              </div>
              <Show
                when={!pack().owned}
                fallback={<HeroOwnedPill testId="starter-pack-owned" />}
              >
                <button
                  type="button"
                  data-testid="starter-pack-purchase"
                  onClick={() => props.onPurchase(pack())}
                  class="pointer-events-auto relative flex h-10 w-full items-center justify-center rounded-3xl bg-white/90 px-2 text-base font-bold text-[#00524F]"
                >
                  <span data-testid="starter-pack-price">
                    {pack().priceLocalized}
                  </span>
                </button>
                <HeroCountdown
                  color={pack().display.countdownColor}
                  endMs={pack().eligibleUntilMs}
                  serverNow={props.serverNow}
                />
              </Show>
            </div>
          </>
        )}
      </Show>
    </HeroCardFrame>
  )
}

// Whether the starter-pack slot holds a card at server time nowMs: an
// omitted slot never does; a pending one shows its skeleton; an owned pack
// stays for as long as the server returns it; an unowned one goes the moment
// the clock passes its eligible_until.
function starterPackShowing(
  slot: StarterPackSlot | undefined,
  nowMs: number,
): boolean {
  if (!slot || slot.status === 'omitted') return false
  const pack = slot.pack
  if (!pack || pack.owned) return true
  return pack.eligibleUntilMs === undefined || nowMs < pack.eligibleUntilMs
}

// The Featured tab's hero row (GLOSSARY.md "Main product collection"): the
// main-product hero cards in display_order ascending, led by the starter-pack
// card when there is one (trailed by it once owned), on a plain horizontal
// scroll — no snap, no swipe gestures. A main product's card body and price
// pill are one tap target and both open the product detail; the starter
// pack's pill buys it.
export function HeroRow(props: {
  heroSlots: HeroSlot[]
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  onPurchaseStarterPack: (pack: StarterPack) => void
  onSelect: (product: CatalogueProduct) => void
  onSelectStarterPack: (pack: StarterPack) => void
  serverNow: Accessor<number>
  starterPack?: StarterPackSlot
  // True while the starter pack shimmer must wait for the offer dialog.
  starterPackShimmerHeld: () => boolean
}) {
  const showStarterPack = createMemo(() =>
    starterPackShowing(props.starterPack, props.serverNow()),
  )
  // A hero disappears the moment the Shop server clock passes its
  // purchase_end_at (GLOSSARY.md "Hero countdown"); filtering preserves the
  // fixed slot order.
  const liveSlots = createMemo(() =>
    props.heroSlots.filter(
      (hero) =>
        hero.purchaseEndAtMs === undefined ||
        props.serverNow() < hero.purchaseEndAtMs,
    ),
  )
  // The heroes still showing: live and not omitted, a pending skeleton
  // counting as one. An empty hero collection hides the whole row
  // (GLOSSARY.md "Empty Shop collection"); pending slots keep it visible as
  // skeletons.
  const showingCount = createMemo(
    () =>
      liveSlots().filter((hero) => hero.slot.status !== 'omitted').length +
      (showStarterPack() ? 1 : 0),
  )
  // A lone hero fills the row's width; two or more sit at a fixed card width
  // on the horizontal scroll (GLOSSARY.md "Main product collection"). Only the
  // <li> changes: the card is already full-width inside it, and a lone
  // full-width item leaves the <ul> nothing to scroll.
  const layout = createMemo(() => (showingCount() === 1 ? 'fill' : 'scroll'))
  const slotClass = () => HERO_SLOT_CLASS[layout()]
  // The starter-pack card leads the row until owned, then trails it — a
  // pending slot leads, its ownership not yet known. Moving remounts the
  // card; its banner is in the memory cache by then, so it shows at once.
  const starterPackOwned = () => props.starterPack?.pack?.owned === true
  const starterPackItem = () => (
    <Show when={showStarterPack() && props.starterPack}>
      {(slot) => (
        <li class={slotClass()}>
          <StarterPackCard
            onPurchase={props.onPurchaseStarterPack}
            onSelect={props.onSelectStarterPack}
            serverNow={props.serverNow}
            shimmerHeld={props.starterPackShimmerHeld}
            slot={slot()}
          />
        </li>
      )}
    </Show>
  )

  return (
    <Show when={showingCount() > 0}>
      <section data-testid="hero-row" class={HERO_ROW_SECTION_CLASS}>
        <ul data-layout={layout()} class={HERO_ROW_LIST_CLASS}>
          <Show when={!starterPackOwned()}>{starterPackItem()}</Show>
          <For each={liveSlots()}>
            {(hero) => (
              // One card per slot from creation: a pending slot and its
              // settled card are the same element, so the banner download
              // started behind the bars is the one the card shows.
              <Show when={hero.slot.status !== 'omitted'}>
                <li class={slotClass()}>
                  <HeroCard
                    hero={hero}
                    isOwned={props.isOwned}
                    onSelect={props.onSelect}
                    serverNow={props.serverNow}
                  />
                </li>
              </Show>
            )}
          </For>
          <Show when={starterPackOwned()}>{starterPackItem()}</Show>
        </ul>
      </section>
    </Show>
  )
}

/**
 * The hero row as the boot skeleton draws it: one card on the scroll and
 * the next card's edge, each the real card's frame with its pending bar, on
 * the design's lighter skeleton fill (no banner sits beneath it).
 */
export function HeroRowPlaceholder() {
  const card = () => (
    <li class={HERO_SLOT_CLASS.scroll}>
      <div class={cn(HERO_CARD_FRAME_CLASS, 'bg-white/50')}>
        <HeroPendingBars />
      </div>
    </li>
  )
  return (
    <section class={HERO_ROW_SECTION_CLASS}>
      <ul class={HERO_ROW_LIST_CLASS}>
        {card()}
        {card()}
      </ul>
    </section>
  )
}
